import type { WrittenPage } from '../../sections'

/**
 * "Mastering the Art of Digital Transformation" e-kitabı — Türkçe.
 *
 * Bölüm sırası, görseller ve maddeler İngilizcesiyle birebir aynı; değişen
 * yalnızca nasıl söylendiği. E-kitabın adı kapağında basılı olduğu için
 * İngilizce kalıyor; "change management", "benchmark", "governance" gibi
 * yerleşik terimler de öyle.
 *
 * İndirilen dosya İngilizce — e-kitabın Türkçesi yok. Bu, sayfayı Türkçe
 * yazmamak için bir sebep değil: sayfayı okuyup indirme kararını veren kişi
 * Türkiye'deki okur, ve karar Türkçe veriliyor.
 *
 * Formla ilgili açık soru İngilizce sayfada duruyor ve orada bir kez duruyor:
 * `check` iki dilde tekrarlanınca aynı karar iki not olarak sayılıyor.
 */

/** İngilizce sayfadaki dosyanın aynısı; e-kitabın tek bir PDF'i var. */
const PDF
  = 'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/ebook/'
    + '300ca4f7-0413-410e-9fce-24e4164020b6-Mastering%20the%20Art%20of%20Digital%20Transformation.pdf'

export const ebookMasteringDigitalTransformation: WrittenPage = {
  navLabel: 'Dijital dönüşüm sanatı',
  title: 'Mastering the Art of Digital Transformation',
  description:
    'Karmaşayı yönetin, ekipleri hizalayın, gerçek dönüşümü hayata geçirin. Stratejiden '
    + 'uygulamaya geçmenin pratik rehberini Digitopia Impact Labs’ten indirin.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Stratejiden başarıya',
      headline: 'Mastering the Art of Digital Transformation',
      sub: 'Karmaşayı yönetin. Ekipleri hizalayın. Gerçek dönüşümü hayata geçirin.',
      body: [
        'Dijital dönüşüm artık bir tercih değil; ama pek çok kurum stratejiyi sonuca çevirmekte '
          + 'zorlanıyor. Bu e-kitap, başarılı uygulamanın arkasında gerçekte ne olduğunu '
          + 'anlatıyor ve planlamadan gerçek ilerlemeye geçmenize yardım ediyor.',
      ],
      ctas: [{ label: 'İndirin', href: PDF }],
      photo: {
        src: '/photo/mastering-digital-transformation-ebook-artwork.webp',
        alt:
          'E-kitabın kapak görseli: karanlık bir merkezden dışa doğru kıvrılan, uzun pozlamayla '
          + 'çekilmiş mavi ve beyaz ışık izleri.',
      },
      photoFrame: 'square',
    },
    {
      kind: 'cta',
      tone: 'card',
      flip: true,
      heading: 'Neden indirmelisiniz?',
      sub:
        'Bu e-kitap; dönüşüm liderleri ve dijital yöneticiler için vizyonla uygulama arasındaki '
        + 'boşluğu kapatmayı anlatan pratik bir rehber.',
      points: [
        'Dönüşümlerin çoğu neden başarısız oluyor, bundan nasıl kaçınılır?',
        'İnsanı, süreci ve teknolojiyi ortak bir hedef etrafında nasıl hizalarsınız?',
        'Sahadan gerçek başarı örneklerinden ne öğrenebilirsiniz?',
        'Bu hızlı değişen ortamda değişimi nasıl net bir şekilde yönetirsiniz?',
      ],
      ctas: [{ label: 'Rehberi indirin', href: PDF }],
      photo: {
        src: '/photo/mastering-digital-transformation-ebook-covers.webp',
        alt:
          'Mastering the Art of Digital Transformation’ın iki kopyası, biri diğerinin arkasında, '
          + 'beyaz zeminde.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Neler öğreneceksiniz?',
      points: [
        'Başarılı bir dönüşümün temel bileşenleri: öne çıkan kurumları ayıran kritik başarı '
          + 'faktörlerini anlayın.',
        'Hizalanmış bir dönüşüm stratejisi nasıl kurulur: ekipleri ve liderliği net ve ortak bir '
          + 'yön etrafında toplamanın pratik yolları.',
        'Uygulama boşluğunu kapatmak: plandan eyleme nasıl geçilir, momentum nasıl korunur?',
        'Change management’te iyi uygulamalar: dirençle baş edin, karmaşayı güvenle yönetin.',
        'Benchmark’lar ve sahadan içgörüler: diğer şirketler nerede duruyor, yolculuklarından ne '
          + 'öğrenilir?',
      ],
      ctas: [{ label: 'Rehberi indirin', href: PDF }],
      photo: {
        src: '/photo/mastering-digital-transformation-ebook-stack.webp',
        alt:
          'Mastering the Art of Digital Transformation’ın basılı kopyalarından oluşan bir yığın, '
          + 'yukarıdan görünüm.',
      },
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'Başarılı dönüşümün rehberine ulaşın',
      sub:
        'E-kitabı indirin ve gerçekten fark yaratan bir dönüşüm için bir sonraki adımı atın.',
      cta: { label: 'Rehberi indirin', href: PDF },
    },
    {
      kind: 'quotes',
      variant: 'fame',
      heading: 'Müşterilerimiz ne diyor?',
      items: [
        {
          quote:
            'Dijital olgunluk değerlendirmesi, departmanlarımızı ortak hedefler etrafında '
            + 'harekete geçirmekte bizim için kritik bir araç oldu. Digitopia’nın Digital '
            + 'Maturity Index’i sayesinde müşteri, governance, operasyon, insan, inovasyon ve '
            + 'teknoloji gibi temel alanlarda nerede durduğumuzu net biçimde gördük. Bir '
            + 'moderatörün yönlendirmesi ve rekabetçi içgörülerle ekiplerimiz kendilerini pazar '
            + 'içinde konumlandırabildi ve dönüşüm adımlarının sahipliğini üstlendi. '
            + 'Departmanlar arasında hesap verebilirliği kurduk ve stratejik hizalanmayı '
            + 'sağladık.',
          source: 'Tarkan Ateşoğlu, Information Technology Group Manager',
          company: 'Lila Kağıt',
          logo: { name: 'Lila Kağıt', src: '/logo/lilakagitcard.png' },
          portrait: { src: '/photo/portrait-tarkan-atesoglu.png', alt: 'Tarkan Ateşoğlu' },
        },
      ],
    },
  ],
}
